import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import {
  FiTrash2,
  FiAlertTriangle,
  FiCheckCircle,
  FiX,
  FiShield,
  FiLock,
  FiUnlock,
  FiClock,
  FiSave,
  FiEye,
  FiEyeOff,
  FiKey,
  FiCheck,
  FiCopy,
  FiZap,
  FiMail,
  FiSend,
} from "react-icons/fi";

const SystemSettings = ({ onSystemDataReset }) => {
  const { token, setToken } = useAuth();
  const [resetLoading, setResetLoading] = useState(false);
  const [feedback, setFeedback] = useState({ text: "", type: "" });
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  // Registration Control State
  const [regStatus, setRegStatus] = useState("open"); // "open" | "upcoming" | "closed"
  const [regMessage, setRegMessage] = useState("Registrations are now open!");
  const [regLoading, setRegLoading] = useState(false);

  // Admin Password Change State
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Email Diagnostics State
  const [testEmail, setTestEmail] = useState("convocationmuj@gmail.com");
  const [emailTesting, setEmailTesting] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const handleSendTestEmail = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!testEmail || !testEmail.includes("@")) {
      setEmailResult({
        success: false,
        message: "Please enter a valid recipient email address (e.g. yourname@domain.com)",
      });
      return;
    }

    try {
      setEmailTesting(true);
      setEmailResult(null);
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/auth/test-email`,
        { recipientEmail: testEmail },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setEmailResult({
        success: true,
        message: res.data?.message || `Test email sent successfully to ${testEmail}!`,
        data: res.data?.data,
      });
    } catch (err) {
      setEmailResult({
        success: false,
        message:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message ||
          "Failed to send test email. Please check SMTP configuration.",
      });
    } finally {
      setEmailTesting(false);
    }
  };

  // Generate strong random password
  const generateStrongPassword = () => {
    const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const lower = "abcdefghijkmnopqrstuvwxyz";
    const numbers = "23456789";
    const symbols = "!@#$%^&*()_+";

    let pass = "";
    pass += upper[Math.floor(Math.random() * upper.length)];
    pass += upper[Math.floor(Math.random() * upper.length)];
    pass += lower[Math.floor(Math.random() * lower.length)];
    pass += lower[Math.floor(Math.random() * lower.length)];
    pass += numbers[Math.floor(Math.random() * numbers.length)];
    pass += numbers[Math.floor(Math.random() * numbers.length)];
    pass += symbols[Math.floor(Math.random() * symbols.length)];
    pass += symbols[Math.floor(Math.random() * symbols.length)];

    const allChars = upper + lower + numbers + symbols;
    for (let i = 0; i < 4; i++) {
      pass += allChars[Math.floor(Math.random() * allChars.length)];
    }

    const shuffled = pass
      .split("")
      .sort(() => 0.5 - Math.random())
      .join("");

    setNewPassword(shuffled);
    setConfirmPassword(shuffled);
    setShowNewPassword(true);
    setShowConfirmPassword(true);
  };

  // Copy password to clipboard
  const handleCopyPassword = () => {
    if (!newPassword) return;
    navigator.clipboard.writeText(newPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  // Strong password checks
  const pwdHasMinLength = newPassword.length >= 8;
  const pwdHasUppercase = /[A-Z]/.test(newPassword);
  const pwdHasLowercase = /[a-z]/.test(newPassword);
  const pwdHasNumber = /[0-9]/.test(newPassword);
  const pwdHasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword);
  const pwdMatches = newPassword.length > 0 && newPassword === confirmPassword;

  const pwdScore =
    (pwdHasMinLength ? 1 : 0) +
    (pwdHasUppercase ? 1 : 0) +
    (pwdHasLowercase ? 1 : 0) +
    (pwdHasNumber ? 1 : 0) +
    (pwdHasSpecial ? 1 : 0);

  const isPasswordValid = pwdScore === 5 && pwdMatches;

  const handleChangeAdminPassword = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!currentPassword) {
      setFeedback({ text: "Please enter your current admin password.", type: "danger" });
      return;
    }
    if (pwdScore < 5) {
      setFeedback({
        text: "New password must be strong (8+ chars, uppercase, lowercase, number, special symbol).",
        type: "danger",
      });
      return;
    }
    if (!pwdMatches) {
      setFeedback({ text: "New password and confirmation do not match.", type: "danger" });
      return;
    }

    try {
      setPwdLoading(true);
      setFeedback({ text: "", type: "" });
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE}/auth/updatePassword`,
        { currentPassword, newPassword },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data && res.data.token) {
        setToken(res.data.token);
        const savedAuth = JSON.parse(localStorage.getItem("login") || "{}");
        savedAuth.clienttoken = res.data.token;
        localStorage.setItem("login", JSON.stringify(savedAuth));
      }

      setFeedback({
        text: "Admin password successfully updated to strong password!",
        type: "success",
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setFeedback({
        text:
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to update password.",
        type: "danger",
      });
    } finally {
      setPwdLoading(false);
    }
  };

  // Fetch current registration status
  const fetchRegistrationStatus = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/auth/registration-status`
      );
      if (res.data && res.data.success) {
        const currentStatus = res.data.status || (res.data.is_open ? "open" : "closed");
        setRegStatus(currentStatus);
        setRegMessage(
          res.data.message ||
            (currentStatus === "open"
              ? "Registrations are now open!"
              : currentStatus === "upcoming"
              ? "Registrations will open soon!"
              : "Registrations are now closed!")
        );
      }
    } catch (err) {
      console.error("Failed to fetch registration status:", err);
    }
  };

  useEffect(() => {
    fetchRegistrationStatus();
  }, []);

  const handleSelectStatus = (newStatus) => {
    setRegStatus(newStatus);
    if (newStatus === "open") {
      setRegMessage("Registrations are now open!");
    } else if (newStatus === "upcoming") {
      setRegMessage("Registrations will open soon!");
    } else if (newStatus === "closed") {
      setRegMessage("Registrations are now closed!");
    }
  };

  // Execute save registration status
  const handleSaveRegistrationStatus = async () => {
    try {
      setRegLoading(true);
      await axios.post(
        `${process.env.REACT_APP_API_BASE}/auth/toggle-registration`,
        {
          status: regStatus,
          is_open: regStatus === "open",
          message: regMessage,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setFeedback({
        text: `Registration status updated to "${regStatus.toUpperCase()}" with headline: "${regMessage}". Public portal updated!`,
        type: "success",
      });
    } catch (err) {
      setFeedback({
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to update registration status",
        type: "danger",
      });
    } finally {
      setRegLoading(false);
    }
  };

  const handleExecuteReset = async () => {
    if (confirmText.trim().toUpperCase() !== "CONFIRM") {
      return;
    }

    try {
      setResetLoading(true);
      setFeedback({ text: "", type: "" });
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/auth/reset-database`,
        {
          target: "all",
          confirmation: "CONFIRM",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setFeedback({
        text:
          res.data?.message ||
          "Database cleared successfully: All students, departments, dues, and feedbacks reset.",
        type: "success",
      });
      setModalOpen(false);
      setConfirmText("");
      if (onSystemDataReset) {
        onSystemDataReset();
      }
    } catch (err) {
      setFeedback({
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to reset database. Please try again.",
        type: "danger",
      });
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: "1320px", margin: "0 auto", padding: "0 12px" }}>
      {/* Feedback Alert if any */}
      {feedback.text && (
        <div
          style={{
            padding: "12px 16px",
            borderRadius: "8px",
            marginBottom: "20px",
            backgroundColor: feedback.type === "success" ? "#ecfdf5" : "#fef2f2",
            border:
              feedback.type === "success"
                ? "1px solid #a7f3d0"
                : "1px solid #fecaca",
            color: feedback.type === "success" ? "#065f46" : "#991b1b",
            fontSize: "13px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {feedback.type === "success" ? (
              <FiCheckCircle size={16} />
            ) : (
              <FiAlertTriangle size={16} />
            )}
            <span>{feedback.text}</span>
          </div>
          <button
            onClick={() => setFeedback({ text: "", type: "" })}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "14px",
              color: "inherit",
              display: "flex",
              alignItems: "center",
              padding: "2px",
            }}
          >
            <FiX size={15} />
          </button>
        </div>
      )}

      {/* 2-COLUMN GRID SYSTEM LAYOUT */}
      <div className="system-settings-grid">
        {/* CARD 1: DYNAMIC REGISTRATION CONTROL */}
        <div
          style={{
            background: "#fff",
            borderRadius: "12px",
            padding: "26px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "20px",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div>
            <h2
              style={{
                fontSize: "16.5px",
                fontWeight: 600,
                color: "#0f172a",
                margin: "0 0 4px 0",
              }}
            >
              Convocation Registration Control
            </h2>
            <p
              style={{
                fontSize: "13px",
                color: "#64748b",
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              Control registration availability and customize the announcement headline shown on the public student login page.
            </p>
          </div>

          {/* Status Badge */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 14px",
              borderRadius: "20px",
              fontSize: "12.5px",
              fontWeight: 700,
              letterSpacing: "0.4px",
              backgroundColor:
                regStatus === "open"
                  ? "#ecfdf5"
                  : regStatus === "upcoming"
                  ? "#fffbeb"
                  : "#fef2f2",
              color:
                regStatus === "open"
                  ? "#047857"
                  : regStatus === "upcoming"
                  ? "#b45309"
                  : "#b91c1c",
              border:
                regStatus === "open"
                  ? "1px solid #a7f3d0"
                  : regStatus === "upcoming"
                  ? "1px solid #fde68a"
                  : "1px solid #fca5a5",
            }}
          >
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor:
                  regStatus === "open"
                    ? "#10b981"
                    : regStatus === "upcoming"
                    ? "#f59e0b"
                    : "#ef4444",
              }}
            />
            {regStatus === "open"
              ? "OPEN"
              : regStatus === "upcoming"
              ? "OPENING SOON"
              : "CLOSED"}
          </div>
        </div>

        {/* Status Selection Cards */}
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              fontSize: "12.5px",
              fontWeight: 600,
              color: "#334155",
              marginBottom: "8px",
            }}
          >
            Select Portal Status Mode:
          </label>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
            }}
          >
            {/* Option 1: Open */}
            <div
              onClick={() => handleSelectStatus("open")}
              style={{
                border:
                  regStatus === "open"
                    ? "2px solid #059669"
                    : "1px solid #e2e8f0",
                backgroundColor:
                  regStatus === "open" ? "#f0fdf4" : "#f8fafc",
                borderRadius: "10px",
                padding: "14px 16px",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontWeight: 600,
                  fontSize: "13.5px",
                  color: regStatus === "open" ? "#047857" : "#334155",
                  marginBottom: "4px",
                }}
              >
                <FiUnlock size={16} />
                <span>Registrations Open</span>
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: "12px",
                  color: "#64748b",
                  lineHeight: 1.4,
                }}
              >
                Graduating students can sign up, generate passwords, and submit attendance.
              </p>
            </div>

            {/* Option 2: Opening Soon */}
            <div
              onClick={() => handleSelectStatus("upcoming")}
              style={{
                border:
                  regStatus === "upcoming"
                    ? "2px solid #d97706"
                    : "1px solid #e2e8f0",
                backgroundColor:
                  regStatus === "upcoming" ? "#fffbeb" : "#f8fafc",
                borderRadius: "10px",
                padding: "14px 16px",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontWeight: 600,
                  fontSize: "13.5px",
                  color: regStatus === "upcoming" ? "#b45309" : "#334155",
                  marginBottom: "4px",
                }}
              >
                <FiClock size={16} />
                <span>Opening Soon</span>
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: "12px",
                  color: "#64748b",
                  lineHeight: 1.4,
                }}
              >
                Informs students that registrations will open shortly. Sign-ups remain locked.
              </p>
            </div>

            {/* Option 3: Closed */}
            <div
              onClick={() => handleSelectStatus("closed")}
              style={{
                border:
                  regStatus === "closed"
                    ? "2px solid #dc2626"
                    : "1px solid #e2e8f0",
                backgroundColor:
                  regStatus === "closed" ? "#fef2f2" : "#f8fafc",
                borderRadius: "10px",
                padding: "14px 16px",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontWeight: 600,
                  fontSize: "13.5px",
                  color: regStatus === "closed" ? "#b91c1c" : "#334155",
                  marginBottom: "4px",
                }}
              >
                <FiLock size={16} />
                <span>Registrations Closed</span>
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: "12px",
                  color: "#64748b",
                  lineHeight: 1.4,
                }}
              >
                Registrations have ended. Students cannot create new accounts or update choices.
              </p>
            </div>
          </div>
        </div>

        {/* Headline Input Editor */}
        <div style={{ marginBottom: "18px" }}>
          <label
            htmlFor="regMessageInput"
            style={{
              display: "block",
              fontSize: "12.5px",
              fontWeight: 600,
              color: "#334155",
              marginBottom: "6px",
            }}
          >
            Custom Announcement Headline (Displayed in Public Portal):
          </label>
          <input
            id="regMessageInput"
            type="text"
            value={regMessage}
            onChange={(e) => setRegMessage(e.target.value)}
            placeholder="e.g. Registrations are now open!"
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "10px 14px",
              fontSize: "13.5px",
              borderRadius: "8px",
              border: "1px solid #cbd5e1",
              outline: "none",
              color: "#1e293b",
            }}
          />
        </div>

        {/* Live Preview Box */}
        <div
          style={{
            backgroundColor:
              regStatus === "open"
                ? "#f0fdf4"
                : regStatus === "upcoming"
                ? "#fffbeb"
                : "#fff1f2",
            border:
              regStatus === "open"
                ? "1px solid #bbf7d0"
                : regStatus === "upcoming"
                ? "1px solid #fde68a"
                : "1px solid #fecdd3",
            borderRadius: "8px",
            padding: "12px 16px",
            fontSize: "12.5px",
            color:
              regStatus === "open"
                ? "#166534"
                : regStatus === "upcoming"
                ? "#92400e"
                : "#9f1239",
            lineHeight: 1.5,
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <FiEye size={16} style={{ flexShrink: 0 }} />
          <span>
            <strong>Public Portal Preview:</strong> The student login page will display:{" "}
            <span style={{ fontWeight: 700 }}>
              "{regMessage || "No headline set"}"
            </span>
          </span>
        </div>

        {/* Action Save Button */}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={handleSaveRegistrationStatus}
            disabled={regLoading}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 24px",
              fontSize: "13px",
              fontWeight: 600,
              color: "#fff",
              backgroundColor:
                regStatus === "open"
                  ? "#059669"
                  : regStatus === "upcoming"
                  ? "#d97706"
                  : "#dc2626",
              border: "none",
              borderRadius: "6px",
              cursor: regLoading ? "not-allowed" : "pointer",
              transition: "opacity 0.15s ease",
            }}
          >
            <FiSave size={15} />
            {regLoading ? "Saving Changes..." : "Save Status Settings"}
          </button>
        </div>
      </div>

      {/* CARD 2: ADMIN PASSWORD & SECURITY */}
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "26px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <FiKey size={18} color="#e06d15" />
            <h2
              style={{
                fontSize: "16.5px",
                fontWeight: 600,
                color: "#0f172a",
                margin: 0,
              }}
            >
              Admin Password & Security
            </h2>
          </div>
          <p
            style={{
              fontSize: "13px",
              color: "#64748b",
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            Change your administrator login password. Ensure you choose a strong password to safeguard the convocation management system.
          </p>
        </div>

        <form onSubmit={handleChangeAdminPassword}>
          {/* Quick Generator & Copy Toolbar */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "8px",
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              padding: "10px 14px",
              marginBottom: "16px",
            }}
          >
            <div style={{ fontSize: "12.5px", color: "#475569", fontWeight: 500 }}>
              Need a safe password? Generate a randomized strong password instantly:
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                type="button"
                onClick={generateStrongPassword}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "6px 12px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#e06d15",
                  backgroundColor: "#fff",
                  border: "1px solid #fed7aa",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                }}
              >
                <FiZap size={13} />
                Generate Strong Password
              </button>

              {newPassword && (
                <button
                  type="button"
                  onClick={handleCopyPassword}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "6px 12px",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: copied ? "#059669" : "#334155",
                    backgroundColor: copied ? "#ecfdf5" : "#fff",
                    border: copied ? "1px solid #a7f3d0" : "1px solid #cbd5e1",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  }}
                >
                  {copied ? <FiCheck size={13} /> : <FiCopy size={13} />}
                  {copied ? "Copied to Clipboard!" : "Copy Password"}
                </button>
              )}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "16px",
              marginBottom: "16px",
            }}
          >
            {/* Current Password */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: 600,
                  color: "#334155",
                  marginBottom: "6px",
                }}
              >
                Current Admin Password:
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "9px 38px 9px 12px",
                    fontSize: "13.5px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    outline: "none",
                    color: "#1e293b",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#64748b",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {showCurrentPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "6px",
                }}
              >
                <label
                  style={{
                    fontSize: "12.5px",
                    fontWeight: 600,
                    color: "#334155",
                    margin: 0,
                  }}
                >
                  New Strong Password:
                </label>
              </div>
              <div style={{ position: "relative" }}>
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new strong password"
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "9px 38px 9px 12px",
                    fontSize: "13.5px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    outline: "none",
                    color: "#1e293b",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#64748b",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {showNewPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>

            {/* Confirm New Password */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: 600,
                  color: "#334155",
                  marginBottom: "6px",
                }}
              >
                Confirm New Password:
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "9px 38px 9px 12px",
                    fontSize: "13.5px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    outline: "none",
                    color: "#1e293b",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#64748b",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {showConfirmPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>
          </div>

          {/* Password Strength Meter & Guidelines */}
          {newPassword && (
            <div
              style={{
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "14px 16px",
                marginBottom: "18px",
              }}
            >
              {/* Strength Bar */}
              <div style={{ marginBottom: "10px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "12px",
                    fontWeight: 600,
                    marginBottom: "4px",
                    color:
                      pwdScore <= 2
                        ? "#dc2626"
                        : pwdScore <= 4
                        ? "#d97706"
                        : "#059669",
                  }}
                >
                  <span>Password Strength:</span>
                  <span>
                    {pwdScore <= 1
                      ? "Very Weak"
                      : pwdScore === 2
                      ? "Weak"
                      : pwdScore === 3
                      ? "Fair"
                      : pwdScore === 4
                      ? "Good"
                      : "Strong & Secure"}
                  </span>
                </div>
                <div
                  style={{
                    width: "100%",
                    height: "6px",
                    backgroundColor: "#e2e8f0",
                    borderRadius: "3px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${(pwdScore / 5) * 100}%`,
                      backgroundColor:
                        pwdScore <= 2
                          ? "#ef4444"
                          : pwdScore <= 4
                          ? "#f59e0b"
                          : "#10b981",
                      transition: "width 0.3s ease, background-color 0.3s ease",
                    }}
                  />
                </div>
              </div>

              {/* Requirements Checklist */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "6px 12px",
                  fontSize: "11.5px",
                }}
              >
                <div style={{ color: pwdHasMinLength ? "#166534" : "#64748b", display: "flex", alignItems: "center", gap: "6px" }}>
                  {pwdHasMinLength ? (
                    <FiCheck size={13} style={{ color: "#059669", flexShrink: 0 }} />
                  ) : (
                    <span style={{ width: 13, height: 13, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "#94a3b8" }} />
                    </span>
                  )}
                  <span>At least 8 characters</span>
                </div>

                <div style={{ color: pwdHasUppercase ? "#166534" : "#64748b", display: "flex", alignItems: "center", gap: "6px" }}>
                  {pwdHasUppercase ? (
                    <FiCheck size={13} style={{ color: "#059669", flexShrink: 0 }} />
                  ) : (
                    <span style={{ width: 13, height: 13, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "#94a3b8" }} />
                    </span>
                  )}
                  <span>Uppercase letter (A-Z)</span>
                </div>

                <div style={{ color: pwdHasLowercase ? "#166534" : "#64748b", display: "flex", alignItems: "center", gap: "6px" }}>
                  {pwdHasLowercase ? (
                    <FiCheck size={13} style={{ color: "#059669", flexShrink: 0 }} />
                  ) : (
                    <span style={{ width: 13, height: 13, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "#94a3b8" }} />
                    </span>
                  )}
                  <span>Lowercase letter (a-z)</span>
                </div>

                <div style={{ color: pwdHasNumber ? "#166534" : "#64748b", display: "flex", alignItems: "center", gap: "6px" }}>
                  {pwdHasNumber ? (
                    <FiCheck size={13} style={{ color: "#059669", flexShrink: 0 }} />
                  ) : (
                    <span style={{ width: 13, height: 13, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "#94a3b8" }} />
                    </span>
                  )}
                  <span>Number (0-9)</span>
                </div>

                <div style={{ color: pwdHasSpecial ? "#166534" : "#64748b", display: "flex", alignItems: "center", gap: "6px" }}>
                  {pwdHasSpecial ? (
                    <FiCheck size={13} style={{ color: "#059669", flexShrink: 0 }} />
                  ) : (
                    <span style={{ width: 13, height: 13, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "#94a3b8" }} />
                    </span>
                  )}
                  <span>Special character (!@#$%^&*)</span>
                </div>

                <div style={{ color: pwdMatches ? "#166534" : "#64748b", display: "flex", alignItems: "center", gap: "6px" }}>
                  {pwdMatches ? (
                    <FiCheck size={13} style={{ color: "#059669", flexShrink: 0 }} />
                  ) : (
                    <span style={{ width: 13, height: 13, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "#94a3b8" }} />
                    </span>
                  )}
                  <span>Passwords match</span>
                </div>
              </div>
            </div>
          )}

          {/* Action Button */}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="submit"
              disabled={pwdLoading || !currentPassword || !newPassword || !confirmPassword}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 22px",
                fontSize: "13px",
                fontWeight: 600,
                color: "#fff",
                backgroundColor: isPasswordValid && currentPassword ? "#e06d15" : "#94a3b8",
                border: "none",
                borderRadius: "6px",
                cursor:
                  pwdLoading || !currentPassword || !isPasswordValid
                    ? "not-allowed"
                    : "pointer",
                transition: "background-color 0.15s ease",
              }}
            >
              <FiKey size={14} />
              {pwdLoading ? "Updating Password..." : "Update Admin Password"}
            </button>
          </div>
        </form>
      </div>

      {/* CARD 3: EMAIL SERVICE DIAGNOSTICS & HEALTH CHECK */}
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "26px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "16px",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
              <FiMail size={18} color="#2563eb" />
              <h2
                style={{
                  fontSize: "16.5px",
                  fontWeight: 600,
                  color: "#0f172a",
                  margin: 0,
                }}
              >
                Email Service Diagnostics &amp; Health Check
              </h2>
            </div>
            <p
              style={{
                fontSize: "13px",
                color: "#64748b",
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              Test and verify live outbound email dispatch to ensure registration credentials, attendance receipts, and dues notifications are delivering properly.
            </p>
          </div>

          {/* SMTP Status Badge */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "5px 12px",
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: 700,
              backgroundColor: "#eff6ff",
              color: "#1d4ed8",
              border: "1px solid #bfdbfe",
            }}
          >
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#2563eb",
              }}
            />
            SMTP READY
          </div>
        </div>

        {/* Server Config Highlights */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "10px",
            backgroundColor: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "18px",
            fontSize: "12.5px",
          }}
        >
          <div>
            <span style={{ color: "#64748b", display: "block", fontSize: "11px", fontWeight: 600 }}>
              SMTP HOST
            </span>
            <span style={{ color: "#0f172a", fontWeight: 600 }}>smtp.hostinger.com</span>
          </div>
          <div>
            <span style={{ color: "#64748b", display: "block", fontSize: "11px", fontWeight: 600 }}>
              PORT / SECURITY
            </span>
            <span style={{ color: "#0f172a", fontWeight: 600 }}>465 (SSL / Secure)</span>
          </div>
          <div>
            <span style={{ color: "#64748b", display: "block", fontSize: "11px", fontWeight: 600 }}>
              DEFAULT SENDER
            </span>
            <span style={{ color: "#0f172a", fontWeight: 600 }}>noreply@mujconvocation.in</span>
          </div>
        </div>

        {/* Test Form */}
        <form onSubmit={handleSendTestEmail}>
          <div style={{ marginBottom: "14px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "6px",
                flexWrap: "wrap",
                gap: "6px",
              }}
            >
              <label
                style={{
                  fontSize: "12.5px",
                  fontWeight: 600,
                  color: "#334155",
                  margin: 0,
                }}
              >
                Recipient Test Email Address:
              </label>

              {/* Quick Fill Shortcuts */}
              <div style={{ display: "flex", gap: "6px", fontSize: "11px" }}>
                <span style={{ color: "#64748b" }}>Quick Fill:</span>
                <button
                  type="button"
                  onClick={() => setTestEmail("convocationmuj@gmail.com")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#2563eb",
                    textDecoration: "underline",
                    cursor: "pointer",
                    padding: 0,
                    fontSize: "11px",
                    fontWeight: 600,
                  }}
                >
                  convocationmuj@gmail.com
                </button>
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter destination email (e.g. convocationmuj@gmail.com)"
                style={{
                  flex: 1,
                  minWidth: "240px",
                  boxSizing: "border-box",
                  padding: "9px 12px",
                  fontSize: "13.5px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  outline: "none",
                  color: "#1e293b",
                }}
              />
              <button
                type="submit"
                disabled={emailTesting || !testEmail}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "7px",
                  padding: "9px 20px",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#fff",
                  backgroundColor: emailTesting || !testEmail ? "#94a3b8" : "#2563eb",
                  border: "none",
                  borderRadius: "8px",
                  cursor: emailTesting || !testEmail ? "not-allowed" : "pointer",
                  transition: "background-color 0.15s ease",
                  whiteSpace: "nowrap",
                }}
              >
                <FiSend size={13} />
                {emailTesting ? "Sending Test..." : "Send Test Email"}
              </button>
            </div>
          </div>
        </form>

        {/* Live Diagnostics Result Box */}
        {emailResult && (
          <div
            style={{
              marginTop: "16px",
              borderRadius: "8px",
              padding: "14px 16px",
              backgroundColor: emailResult.success ? "#f0fdf4" : "#fef2f2",
              border: emailResult.success ? "1px solid #bbf7d0" : "1px solid #fecaca",
              color: emailResult.success ? "#166534" : "#991b1b",
              fontSize: "12.5px",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: emailResult.data ? "8px" : 0 }}>
              {emailResult.success ? (
                <FiCheckCircle size={16} style={{ flexShrink: 0, marginTop: "2px", color: "#059669" }} />
              ) : (
                <FiAlertTriangle size={16} style={{ flexShrink: 0, marginTop: "2px", color: "#dc2626" }} />
              )}
              <div>
                <strong style={{ display: "block", fontSize: "13px", marginBottom: "2px" }}>
                  {emailResult.success ? "Email Service Operational - Delivery Successful!" : "Email Delivery Test Failed"}
                </strong>
                <span>{emailResult.message}</span>
              </div>
            </div>

            {emailResult.success && emailResult.data && (
              <div
                style={{
                  marginTop: "8px",
                  paddingTop: "8px",
                  borderTop: "1px solid #dcfce7",
                  fontSize: "11.5px",
                  color: "#15803d",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "4px 12px",
                }}
              >
                <div><strong>Recipient:</strong> {emailResult.data.recipient}</div>
                <div><strong>Timestamp:</strong> {emailResult.data.timestamp}</div>
                <div><strong>Message ID:</strong> {emailResult.data.messageId}</div>
                <div><strong>Server Host:</strong> {emailResult.data.smtpHost}:{emailResult.data.smtpPort}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CARD 4: RESET DATABASE */}
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "26px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div style={{ marginBottom: "18px" }}>
            <h2
              style={{
                fontSize: "16.5px",
                fontWeight: 600,
                color: "#0f172a",
                margin: "0 0 6px 0",
              }}
            >
              Reset Database
            </h2>
            <p
              style={{
                fontSize: "13px",
                color: "#64748b",
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              Permanently clear all student records, department accounts, dues, and feedback submissions.
              The primary admin login account will not be deleted.
            </p>
          </div>

          {/* Protected Notice */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 14px",
              backgroundColor: "#f8fafc",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              fontSize: "12.5px",
              color: "#475569",
              marginBottom: "20px",
            }}
          >
            <FiShield size={16} color="#059669" />
            <span>Admin user account is safely preserved.</span>
          </div>
        </div>

        {/* Action Button */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
          <button
            type="button"
            onClick={() => {
              setConfirmText("");
              setModalOpen(true);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "9px 20px",
              fontSize: "13px",
              fontWeight: 600,
              color: "#fff",
              backgroundColor: "#ea580c",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "background-color 0.15s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#c2410c";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#ea580c";
            }}
          >
            <FiTrash2 size={14} />
            Reset Database
          </button>
        </div>
      </div>
      </div>

      {/* Safety Confirmation Modal */}
      {modalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "16px",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "24px",
              width: "100%",
              maxWidth: "440px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
              border: "1px solid #e2e8f0",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "14px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#dc2626",
                  fontWeight: 600,
                  fontSize: "15px",
                }}
              >
                <FiAlertTriangle size={18} />
                <span>Confirm Database Reset</span>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                disabled={resetLoading}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#94a3b8",
                  padding: "4px",
                }}
              >
                <FiX size={18} />
              </button>
            </div>

            {/* Warning Message */}
            <div
              style={{
                backgroundColor: "#fff5f5",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                padding: "12px 14px",
                fontSize: "12.5px",
                color: "#991b1b",
                lineHeight: 1.45,
                marginBottom: "16px",
              }}
            >
              All students, department accounts, dues, and feedback records will be cleared. This action cannot be undone.
            </div>

            {/* Confirmation Input */}
            <div style={{ marginBottom: "18px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: "#334155",
                  marginBottom: "6px",
                }}
              >
                Type <strong style={{ color: "#dc2626" }}>CONFIRM</strong> to proceed:
              </label>
              <input
                type="text"
                placeholder="CONFIRM"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                disabled={resetLoading}
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 12px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  fontSize: "13px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Modal Actions */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                disabled={resetLoading}
                style={{
                  padding: "8px 16px",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#64748b",
                  backgroundColor: "#f1f5f9",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExecuteReset}
                disabled={confirmText.trim().toUpperCase() !== "CONFIRM" || resetLoading}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 18px",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#fff",
                  backgroundColor:
                    confirmText.trim().toUpperCase() === "CONFIRM" && !resetLoading
                      ? "#ea580c"
                      : "#fdba74",
                  border: "none",
                  borderRadius: "6px",
                  cursor:
                    confirmText.trim().toUpperCase() === "CONFIRM" && !resetLoading
                      ? "pointer"
                      : "not-allowed",
                  transition: "background-color 0.15s ease",
                }}
              >
                <FiTrash2 size={13} />
                {resetLoading ? "Clearing..." : "Reset Database"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemSettings;

